import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Paper,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DevicesIcon from '@mui/icons-material/Devices';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import axios from 'axios';
import config from '../config/config';

// Helper para obtener datos amigables y limpios del navegador y SO
function parseDeviceFriendly(deviceName, userAgent) {
  const str = (deviceName && !deviceName.includes('Mozilla')) ? deviceName : (userAgent || deviceName || '');

  let os = 'Dispositivo';
  let deviceType = 'laptop';

  if (/windows/i.test(str)) {
    os = 'Windows';
  } else if (/macintosh|mac os x/i.test(str)) {
    os = 'macOS';
  } else if (/iphone/i.test(str)) {
    os = 'iPhone';
    deviceType = 'phone';
  } else if (/ipad/i.test(str)) {
    os = 'iPad';
    deviceType = 'tablet';
  } else if (/android/i.test(str)) {
    os = 'Android';
    deviceType = 'phone';
  } else if (/linux/i.test(str)) {
    os = 'Linux';
  }

  let browser = 'Navegador Web';
  if (/edg/i.test(str)) {
    browser = 'Microsoft Edge';
  } else if (/chrome|crios/i.test(str)) {
    browser = 'Google Chrome';
  } else if (/firefox|fxios/i.test(str)) {
    browser = 'Mozilla Firefox';
  } else if (/safari/i.test(str) && !/chrome/i.test(str)) {
    browser = 'Apple Safari';
  }

  return {
    os,
    browser,
    deviceType,
    label: `${browser} en ${os}`,
  };
}

export default function ModalDispositivosSeguros({ open, onClose, usuarioId, nombreUsuario }) {
  const [loading, setLoading] = useState(false);
  const [dispositivos, setDispositivos] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0 = Activos, 1 = Historial de revocados
  const [confirmTarget, setConfirmTarget] = useState(null); // null | 'todos' | { id, name }
  const [actionLoading, setActionLoading] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState(null); // { type: 'success'|'error', text }

  const apiUrl = config.apiBaseUrl;

  const fetchDispositivos = useCallback(async () => {
    if (!usuarioId) return;
    setLoading(true);
    try {
      let res = await axios.get(`${apiUrl}/api/login/dispositivos/${usuarioId}`).catch(() => null);
      if (!res || !res.data || !Array.isArray(res.data.dispositivos)) {
        res = await axios.get(`${apiUrl}/api/usuarios/${usuarioId}/dispositivos`);
      }
      if (res && res.data && Array.isArray(res.data.dispositivos)) {
        setDispositivos(res.data.dispositivos);
      } else {
        setDispositivos([]);
      }
    } catch (err) {
      console.error('Error cargando dispositivos:', err);
      setDispositivos([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, usuarioId]);

  useEffect(() => {
    if (open && usuarioId) {
      setConfirmTarget(null);
      setFeedbackMsg(null);
      setActiveTab(0);
      fetchDispositivos();
    }
  }, [open, usuarioId, fetchDispositivos]);

  const handleEjecutarRevocacion = async () => {
    if (!confirmTarget) return;
    const isAll = confirmTarget === 'todos';
    const targetId = isAll ? 'todos' : confirmTarget.id;

    setActionLoading(true);
    try {
      await axios.post(`${apiUrl}/api/login/dispositivos/${usuarioId}/revocar`, {
        deviceId: targetId,
        username: nombreUsuario,
      }).catch(() => {
        return axios.post(`${apiUrl}/api/usuarios/${usuarioId}/dispositivos/revocar`, {
          deviceId: targetId,
          username: nombreUsuario,
        });
      });

      setConfirmTarget(null);
      setFeedbackMsg({
        type: 'success',
        text: isAll
          ? 'Todos los equipos de confianza han sido revocados exitosamente.'
          : `El dispositivo "${confirmTarget.name || 'seleccionado'}" ha sido revocado.`,
      });

      await fetchDispositivos();

      setTimeout(() => {
        setFeedbackMsg(null);
      }, 4000);
    } catch (err) {
      console.error('Error revocando dispositivo:', err);
      setFeedbackMsg({
        type: 'error',
        text: err.response?.data?.message || 'Ocurrió un error al revocar el dispositivo.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatFecha = (str) => {
    if (!str) return '-';
    try {
      const d = new Date(str);
      return d.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return str;
    }
  };

  const getDiasRestantes = (strFechaExpira) => {
    if (!strFechaExpira) return null;
    try {
      const exp = new Date(strFechaExpira).getTime();
      const now = Date.now();
      const diffMs = exp - now;
      if (diffMs <= 0) return 0;
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const getDeviceIcon = (deviceType) => {
    const iconSx = { fontSize: 20, color: '#0655A5' };
    if (deviceType === 'phone') {
      return <PhoneIphoneIcon sx={iconSx} />;
    }
    if (deviceType === 'tablet') {
      return <TabletMacIcon sx={iconSx} />;
    }
    return <LaptopMacIcon sx={iconSx} />;
  };

  const dispositivosActivos = dispositivos.filter((d) => Boolean(d.activo) && Boolean(d.vigente));
  const dispositivosHistorial = dispositivos.filter((d) => !Boolean(d.activo) || !Boolean(d.vigente));
  const listToShow = activeTab === 0 ? dispositivosActivos : dispositivosHistorial;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={actionLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.14)',
          border: '1px solid rgba(226,232,240,0.8)',
          width: '100%',
          maxWidth: { xs: '94%', sm: 620 },
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      {/* HEADER AZUL INSTITUCIONAL */}
      <DialogTitle
        sx={{
          m: 0,
          px: 2.5,
          py: 1.8,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
          color: '#ffffff',
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'rgba(255,255,255,0.18)',
            color: 'white',
            backdropFilter: 'blur(4px)',
            borderRadius: '10px',
          }}
        >
          <DevicesIcon sx={{ fontSize: 20 }} />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '1.05rem',
              fontWeight: 800,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              color: 'white',
            }}
          >
            Dispositivos de Confianza
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.88)',
              fontSize: '0.78rem',
              display: 'block',
              lineHeight: 1.2,
              mt: 0.3,
            }}
          >
            Usuario: <strong style={{ color: '#ffffff' }}>{nombreUsuario || 'Usuario'}</strong>
          </Typography>
        </Box>

        <Tooltip title="Cerrar">
          <IconButton
            size="small"
            onClick={onClose}
            disabled={actionLoading}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      {/* PESTAÑAS: ACTIVOS (X) VS HISTORIAL / AUDITORÍA (Y) */}
      <Box sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {
              minHeight: 42,
              py: 1,
              px: 2,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.80rem',
              color: '#64748b',
              '&.Mui-selected': {
                color: '#0655A5',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0655A5',
              height: 2.5,
              borderRadius: '2px 2px 0 0',
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <span>Equipos Activos</span>
                <Chip
                  size="small"
                  label={dispositivosActivos.length}
                  sx={{
                    height: 18,
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    bgcolor: activeTab === 0 ? '#dcfce7' : '#f1f5f9',
                    color: activeTab === 0 ? '#15803d' : '#64748b',
                  }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <HistoryIcon sx={{ fontSize: 16 }} />
                <span>Historial de Revocados</span>
                <Chip
                  size="small"
                  label={dispositivosHistorial.length}
                  sx={{
                    height: 18,
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    bgcolor: activeTab === 1 ? '#e0f2fe' : '#f1f5f9',
                    color: activeTab === 1 ? '#0369a1' : '#64748b',
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
        {/* BARRA SUPERIOR DE INFORMACIÓN Y ACCIÓN */}
        {activeTab === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              bgcolor: '#ffffff',
              p: 1.5,
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              flexWrap: 'wrap',
              gap: 1.2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 220 }}>
              <SecurityIcon sx={{ fontSize: 20, color: '#0655A5', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.80rem', color: '#475569', lineHeight: 1.4 }}>
                Los equipos autorizados no solicitan código durante <strong>7 días</strong>.
              </Typography>
            </Box>

            {dispositivosActivos.length > 0 && !confirmTarget && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setConfirmTarget('todos')}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.74rem',
                  borderRadius: '8px',
                  py: 0.4,
                  px: 1.2,
                  borderColor: '#fca5a5',
                  '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' },
                }}
              >
                Revocar Todos
              </Button>
            )}
          </Box>
        )}

        {/* MENSAJES DE RETROALIMENTACIÓN */}
        {feedbackMsg && (
          <Box
            sx={{
              mb: 2,
              p: 1.2,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              bgcolor: feedbackMsg.type === 'success' ? '#ecfdf5' : '#fef2f2',
              border: `1px solid ${feedbackMsg.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
              color: feedbackMsg.type === 'success' ? '#065f46' : '#991b1b',
            }}
          >
            {feedbackMsg.type === 'success' ? (
              <CheckCircleIcon sx={{ fontSize: 20, color: '#10b981' }} />
            ) : (
              <WarningAmberRoundedIcon sx={{ fontSize: 20, color: '#ef4444' }} />
            )}
            <Typography sx={{ fontSize: '0.80rem', fontWeight: 600 }}>
              {feedbackMsg.text}
            </Typography>
          </Box>
        )}

        {/* CONFIRMACIÓN INTEGRADA */}
        {confirmTarget && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 1.8,
              borderRadius: '10px',
              bgcolor: '#fff1f2',
              border: '1px solid #fecdd3',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 22, color: '#e11d48', mt: 0.2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, color: '#9f1239', fontSize: '0.86rem' }}>
                  {confirmTarget === 'todos'
                    ? '¿Estás seguro de revocar todos los equipos?'
                    : `¿Revocar confianza para "${confirmTarget.name}"?`}
                </Typography>
                <Typography sx={{ fontSize: '0.76rem', color: '#be123c', mt: 0.4, lineHeight: 1.4 }}>
                  {confirmTarget === 'todos'
                    ? 'El usuario deberá ingresar su código enviado al correo institucional en cualquier equipo en su próximo acceso.'
                    : 'Este equipo dejará de ser recordado y se solicitará código 2FA la próxima vez que inicie sesión.'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={actionLoading}
                    onClick={handleEjecutarRevocacion}
                    sx={{
                      bgcolor: '#dc2626',
                      '&:hover': { bgcolor: '#b91c1c' },
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.74rem',
                      borderRadius: '6px',
                      py: 0.4,
                      px: 1.5,
                      boxShadow: 'none',
                    }}
                  >
                    {actionLoading ? 'Revocando...' : confirmTarget === 'todos' ? 'Sí, revocar todos' : 'Sí, revocar'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={actionLoading}
                    onClick={() => setConfirmTarget(null)}
                    sx={{
                      color: '#475569',
                      borderColor: '#cbd5e1',
                      '&:hover': { bgcolor: '#f1f5f9' },
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.74rem',
                      borderRadius: '6px',
                      py: 0.4,
                      px: 1.2,
                    }}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* LISTADO DE DISPOSITIVOS EN TARJETAS ESTILO SISTEMA */}
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={24} sx={{ color: '#0655A5' }} />
            <Typography sx={{ color: '#64748b', fontSize: '0.84rem', fontWeight: 600 }}>
              Cargando equipos de confianza...
            </Typography>
          </Box>
        ) : listToShow.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center', bgcolor: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            {activeTab === 0 ? (
              <>
                <SecurityIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#475569', fontSize: '0.90rem' }}>
                  No hay equipos activos actualmente
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mt: 0.5, maxWidth: 420, mx: 'auto' }}>
                  Cuando el usuario inicie sesión y seleccione &quot;Confiar en este equipo por 7 días&quot;, su navegador aparecerá listado aquí.
                </Typography>
              </>
            ) : (
              <>
                <HistoryIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#475569', fontSize: '0.90rem' }}>
                  Sin historial de equipos revocados
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mt: 0.5 }}>
                  Todos los equipos revocados o vencidos quedarán registrados aquí para auditoría y trazabilidad.
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {listToShow.map((row) => {
              const esActivoVigente = Boolean(row.activo) && Boolean(row.vigente);
              const info = parseDeviceFriendly(row.deviceName, row.userAgent);
              const diasRestantes = getDiasRestantes(row.fechaExpiracion);

              return (
                <Paper
                  key={row.id}
                  elevation={0}
                  sx={{
                    p: 1.8,
                    borderRadius: '10px',
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      boxShadow: '0 3px 8px rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  {/* DETALLES DEL DISPOSITIVO */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 240 }}>
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        bgcolor: esActivoVigente ? 'rgba(6,85,165,0.08)' : '#f1f5f9',
                        borderRadius: '9px',
                      }}
                    >
                      {getDeviceIcon(info.deviceType)}
                    </Avatar>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#0f172a' }}>
                          {info.label}
                        </Typography>
                        {esActivoVigente ? (
                          <Chip
                            size="small"
                            label={diasRestantes !== null ? `ACTIVO (${diasRestantes}D)` : 'ACTIVO'}
                            sx={{
                              height: 20,
                              fontSize: '0.64rem',
                              fontWeight: 800,
                              bgcolor: '#dcfce7',
                              color: '#15803d',
                              border: '1px solid #bbf7d0',
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label={row.fechaRevocacion ? 'REVOCADO MANUALMENTE' : 'EXPIRADO'}
                            sx={{
                              height: 20,
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              bgcolor: '#fef2f2',
                              color: '#b91c1c',
                              border: '1px solid #fecaca',
                            }}
                          />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.74rem', color: '#64748b', fontFamily: 'monospace' }}>
                          IP: <strong>{row.ip || 'Local / Interna'}</strong>
                        </Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                          Registrado: {formatFecha(row.fechaCreacion)}
                        </Typography>
                        {esActivoVigente ? (
                          <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                            Vence: {formatFecha(row.fechaExpiracion)}
                          </Typography>
                        ) : row.fechaRevocacion ? (
                          <Typography sx={{ fontSize: '0.74rem', color: '#b91c1c', fontWeight: 600 }}>
                            Revocado: {formatFecha(row.fechaRevocacion)}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                            Expiró: {formatFecha(row.fechaExpiracion)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* ACCIÓN (BOTÓN REVOCAR) */}
                  <Box>
                    {esActivoVigente ? (
                      <Tooltip title="Revocar confianza de este equipo" arrow>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() =>
                            setConfirmTarget({
                              id: row.id,
                              name: info.label,
                            })
                          }
                          disabled={actionLoading}
                          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.74rem',
                            fontWeight: 700,
                            py: 0.4,
                            px: 1.3,
                            borderRadius: '7px',
                            borderColor: '#fca5a5',
                            bgcolor: '#fff5f5',
                            '&:hover': {
                              bgcolor: '#fee2e2',
                              borderColor: '#ef4444',
                            },
                          }}
                        >
                          Revocar
                        </Button>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic', px: 1 }}>
                        {row.fechaRevocacion ? 'Acceso revocado' : 'Vigencia vencida'}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.2,
          bgcolor: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Button
          onClick={onClose}
          disabled={actionLoading}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.84rem',
            color: '#475569',
            px: 2.2,
            py: 0.6,
            borderRadius: '8px',
            '&:hover': { bgcolor: '#e2e8f0' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
